import { getAuthHeaders, getRequestProgress } from "../utils/servicesUtils";
import { apiFetch } from "../utils/apiFetch";
// import {
//   fetchMockFilterConfig,
//   fetchMockFilterConfigCategories,
// } from "../utils/mockFilterConfig";

// const FILTERS_MOCK_MODE_STORAGE_KEY = "filtersMockMode";

// const getFiltersMockMode = () => {
//   if (typeof window === "undefined") return null;
//   const params = new URLSearchParams(window.location.search);
//   const urlMode = params.get("filtersMockMode");
//   if (urlMode) return urlMode;
//   return window.localStorage.getItem(FILTERS_MOCK_MODE_STORAGE_KEY);
// };

export const getAllUsers = async () => {
  const ENDPOINT = "/api/users/system-users/";
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to fetch users data");

  return response.json();
};

export const getAllEvaluatorUsers = async () => {
  const ENDPOINT = "/api/users/evaluators/";
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to getSystemEvaluators");

  return response.json();
};

export const getConfigurableSystemUsers = async () => {
  const ENDPOINT = "/api/users/config/users/";
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to fetch frameworks templates data");

  return response.json();
};

// Body expects an array of ids
export const getUserDetails = async (body) => {
  const ENDPOINT = "/api/users/detail/";
  const response = await apiFetch(ENDPOINT, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new Error("Failed to fetch user details");

  return response.json();
}

// Fetch filter configuration for dashboard
export const getFilterConfig = async () => {
  /**
   * Optional frontend-only mock switch to test filter rendering:
   * - localStorage.setItem("filtersMockMode", "legacy")
   * - localStorage.setItem("filtersMockMode", "categories")
   * - localStorage.removeItem("filtersMockMode") // back to real API
   *
   * You can also use URL query param once per load:
   *   ?filtersMockMode=legacy
   *   ?filtersMockMode=categories
   */
  // const mockMode = getFiltersMockMode();
  // if (mockMode === "legacy") {
  //   return fetchMockFilterConfig();
  // }
  // if (mockMode === "categories") {
  //   return fetchMockFilterConfigCategories();
  // }

  const ENDPOINT = `/api/users/system/filters/`;  // Note: trailing slash required
  const response = await apiFetch(ENDPOINT, {
    headers: getAuthHeaders(),
  });

  // Log response details for debugging
  // console.log("🔍 Filter Config Response Status:", response.status);
  // console.log("🔍 Filter Config Response Headers:", response.headers);

  if (!response.ok) {
    const errorText = await response.text();
    console.error("❌ Filter Config Error Response:", errorText);
    throw new Error(`Failed to fetch filter configuration: ${response.status} - ${errorText}`);
  }

  // Check content type before parsing
  const contentType = response.headers.get("content-type");
  // console.log("🔍 Filter Config Content-Type:", contentType);

  if (!contentType || !contentType.includes("application/json")) {
    await response.text();
    throw new Error(`Expected JSON but got ${contentType}`);
  }

  const data = await response.json();
  return data;
};

// Update Password Service: Updates the user password.
export const updatePasswordService = async (email, newPassword) => {
  const response = await apiFetch("/api/users/update-password/", {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, password: newPassword }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "Password update failed");
  }
  return response.json();
};

// Reset Password Service: Reset password by sending email.
export const resetPasswordService = async (email) => {
  const ENDPOINT = `/api/users/reset/password/?user_email=${email}`;

  try {

    const response = await apiFetch(ENDPOINT, {
      method: "PUT",
      headers: getAuthHeaders()
    });

    const responseData = await response.json();
    if (!response.ok) {
      throw responseData;
    }

    return responseData;

  } catch (error) {
    console.error("Error in resetPasswordService:", error);
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while reseting password.");
    }
    throw error;
  }
};

export const updateExistingUser = async (userId, data) => {
  const ENDPOINT = `/api/users/update/${userId}/`;
  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const responseData = await response.json();
    if (!response.ok) {
      throw responseData;
    }
    return responseData;
  } catch (error) {
    console.error("Error in updateExistingUser:", error);
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while trying to update user data.");
    }
    throw error;
  }
};

export const transferObligations = async (user_id, to_user) => {
  const ENDPOINT = `/api/users/transfer/all/${user_id}/${to_user}/`;
  const response = await apiFetch(ENDPOINT, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to transferObligations");
  }
  return response.json();
};

export const registerNewUser = async (payload, onProgress = () => { }) => {
  const ENDPOINT = "/api/users/register/";
  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    // Read the body first
    const contentType = response.headers.get("Content-Type") || "";
    const contentLength = +response.headers.get("Content-Length");

    if (!response.ok) {
      // Try to read JSON response or return generic message
      const errorBody = contentType.includes("application/json")
        ? await response.json()
        : { message: "Unexpected error occurred" };

      throw errorBody;
    }

    // If no content length return normal JSON
    if (!contentLength) {
      return await response.json();
    }

    // If none of the above returned, stream the response and parse it into JSON
    const reader = response.body.getReader();
    const raw = await getRequestProgress(reader, contentLength, onProgress);
    return JSON.parse(raw);
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred trying to register the new user");
    }
    throw error;
  }
};

/**
 * Temporary mock response for the users Excel template.
 * Replace with the real fetch call once the backend endpoint is available.
 */
export const getUsersExcelTemplate = async () => {
  // Example implementation for later:
  // const response = await fetch('/api/users/excel-template/', { headers: getAuthHeaders() });
  // if (!response.ok) throw new Error('Failed to fetch users Excel template');
  // return response.json();

  return {
    columns: [
      { key: "id", label: "ID", type: "number" },
      { key: "email", label: "Email", type: "email" },
      { key: "department", label: "Department", type: "select", source: "departments" },
      { key: "role", label: "Role", type: "select", source: "roles" },
      { key: "active", label: "Active?", type: "boolean" },
    ],
    sources: {
      departments: ["Engineering", "Human Resources", "Finance", "Sales", "Operations"],
      roles: ["Administrator", "Manager", "Developer", "Analyst"],
    },
    rows: [
      { id: 1, email: "user1@example.com", department: "Engineering", role: "Developer", active: true },
      { id: 2, email: "user2@example.com", department: "Human Resources", role: "Manager", active: true },
      { id: 3, email: "user3@example.com", department: "Finance", role: "Analyst", active: false },
      { id: 4, email: "user4@example.com", department: "Sales", role: "Manager", active: true },
      { id: 5, email: "user5@example.com", department: "Operations", role: "Administrator", active: false },
    ],
  };
};

export const deleteUser = async (user_id) => {
  const response = await apiFetch(`/api/users/quick/delete/${user_id}/`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.detail || "Failed to delete user");
    error.status = response.status;
    error.detail = data?.detail;
    throw error;
  }
  return data;
};

export const getUserData = async (user_id) => {
  const ENDPOINT = `/api/users/${user_id}/`;
  const headers = getAuthHeaders();
  try {
    const response = await apiFetch(ENDPOINT, {
      headers: headers,
    });
    const responseData = await response.json();
    if (!response.ok) {
      throw responseData;
    }
    return responseData;
  } catch (error) {
    throw new Error(error);
  }
};

export const setUserPermissions = async (user_id, data) => {
  const ENDPOINT = `/api/users/${user_id}/`;
  const headers = getAuthHeaders();
  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });
    const responseData = await response.json();
    if (!response.ok) {
      throw responseData;
    }
    return responseData;
  } catch (error) {
    console.error("Error in setUserPermissions:", error);
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while setUserPermissions.");
    }
    throw error;
  }
};

/**
 * Quick-create a new user.
 *
 * @param {Object} payload
 * @param {string}   payload.first_name       - required
 * @param {string}   payload.last_name        - required
 * @param {string}   payload.email            - required
 * @param {string}  [payload.middle_name]     - optional
 * @param {string}  [payload.position]        - optional
 * @param {'admin'|'user'} [payload.role]     - optional, defaults to "user"
 * @param {boolean} [payload.global_access]   - optional, defaults to false
 * @param {string[]} [payload.section_id]     - required when global_access is false
 * @param {string[]} [payload.department_id]  - required when global_access is false
 */
export const getScopeUsers = async () => {
  const ENDPOINT = "/api/users/my/scope/users/";
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch scope users");
  return response.json();
};

export const getAllowedUserModules = async () => {
  const ENDPOINT = "/api/users/modules/";
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch allowed modules");
  const data = await response.json();
  return Array.isArray(data?.modules) ? data.modules : [];
};

export const quickCreateUser = async (payload) => {
  const ENDPOINT = "/api/users/quick/create/";
  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while creating the user.");
    }
    throw error;
  }
};

export const removeUserDepartment = async (targetUserId, payload) => {
  const response = await apiFetch(`/api/users/remove/department/${targetUserId}/`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.detail || "Failed to remove department");
    error.status = response.status;
    error.detail = data?.detail;
    throw error;
  }
  return data;
};

export const promoteUserToAdmin = async (targetUserId, { section_ids, department_ids }) => {
  const response = await apiFetch(`/api/users/promote/admin/${targetUserId}/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ section_ids, department_ids }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.detail || "Failed to promote user");
    error.status = response.status;
    error.detail = data?.detail;
    throw error;
  }
  return data;
};

export const assignUserAccess = async (targetUserId, { section_ids = [], department_ids = [] }) => {
  const response = await apiFetch(`/api/users/assign/access/${targetUserId}/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ section_ids, department_ids }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.detail || "Failed to assign access");
    error.status = response.status;
    error.detail = data?.detail;
    throw error;
  }
  return data;
};

export const getHierarchySections = async () => {
  const response = await apiFetch("/api/users/hierarchy/sections/", {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch hierarchy sections");
  return response.json();
};

export const getUserAccessTree = async (user_id) => {
  const ENDPOINT = `/api/users/access-summary-tree/${encodeURIComponent(user_id)}/`;
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to get access tree");

  return response.json();
};

export const exportUsers = async ({ columns, url_expires_in } = {}) => {
  const body = {};
  if (columns?.length) body.columns = columns;
  if (url_expires_in != null) body.url_expires_in = url_expires_in;

  const response = await apiFetch("/api/users/export/users/", {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new Error("Failed to export users");
  return response.json();
};

export const getUsersBySection = async (section_id) => {
  const ENDPOINT = `/api/users/sections/${encodeURIComponent(section_id)}/access/users/`;
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to get users by section");

  return response.json();
};

export const getUsersByDepartment = async (department_id) => {
  const ENDPOINT = `/api/users/departments/${encodeURIComponent(department_id)}/access/users/`;
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to get users by department");

  return response.json();
};

export const getUserDepartmentsBySection = async (user_id, section_id) => {
  const ENDPOINT = `/api/users/${encodeURIComponent(user_id)}/sections/${encodeURIComponent(section_id)}/departments/`;
  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to get user departments by section");

  return response.json();
};